import React, { useMemo, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { calculateSmartTicks } from '../../utils/scaleHelper';
import { isWithin1DBounds } from '../../utils/boundsHelper';

const hexToRgba = (hex: string, alpha: number) => {
  if (!hex) return `rgba(255, 255, 255, ${alpha})`;
  if (hex.startsWith('rgba')) return hex;
  if (hex.startsWith('rgb')) {
    return hex.replace('rgb', 'rgba').replace(')', `, ${alpha})`);
  }
  let c = hex.replace('#', '');
  if (c.length === 3) {
    c = c.split('').map(x => x + x).join('');
  }
  const num = parseInt(c, 16);
  if (isNaN(num)) return `rgba(255, 255, 255, ${alpha})`;
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

interface SideWallGridTextureProps {
  minP: number;
  maxP: number;
  getY: (price: number) => number;
  bottomOfGrid: number;
  topOfGrid: number;
  
  zLimit: number;
  zScale: number;
  getForceZ: (force: number) => number;
  
  sideX: number;
  
  showGrid?: boolean;
  gridColor?: string;
  gridOpacity?: number;
  gridThickness?: number;
  
  targetTicksY?: number;
  /** false: as linhas de preço são desenhadas ao vivo fora da textura (LiveScaleTicks). */
  drawPriceLines?: boolean;
  targetTicksZ?: number;
  /** false: as linhas de força são desenhadas ao vivo fora da textura (LiveScaleTicks). */
  drawForceLines?: boolean;

  colorUp?: string;
  colorDown?: string;
}

export const SideWallGridTexture: React.FC<SideWallGridTextureProps> = ({
  minP,
  maxP,
  getY,
  bottomOfGrid,
  topOfGrid,
  
  zLimit,
  zScale,
  getForceZ,
  
  sideX,
  
  showGrid = true,
  gridColor = '#333333',
  gridOpacity = 0.15,
  gridThickness = 1,
  
  targetTicksY = 8,
  drawPriceLines = true,
  targetTicksZ = 8,
  drawForceLines = true,

  colorUp = '#00ff88',
  colorDown = '#ff0000',
}) => {
  const { gl } = useThree();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const textureRef = useRef<THREE.CanvasTexture | null>(null);
  const meshRef = useRef<THREE.Mesh>(null);

  // Deadzone threshold for minP / maxP (0.5% buffer)
  const lastPRef = useRef<{ minP: number; maxP: number }>({ minP, maxP });
  const thresholdMinP = useRef(minP);
  const thresholdMaxP = useRef(maxP);

  const range = Math.max(maxP - minP, 0.0001);
  const minDiff = Math.abs(minP - lastPRef.current.minP) / range;
  const maxDiff = Math.abs(maxP - lastPRef.current.maxP) / range;

  if (minDiff > 0.005 || maxDiff > 0.005 || thresholdMinP.current === undefined) {
    lastPRef.current = { minP, maxP };
    thresholdMinP.current = minP;
    thresholdMaxP.current = maxP;
  }

  const effectiveMinP = thresholdMinP.current;
  const effectiveMaxP = thresholdMaxP.current;

  // 1. Price Ticks for horizontal grid lines (Y axis mapped to canvas Y)
  const priceTicks = useMemo(() => {
    if (!showGrid || !drawPriceLines || effectiveMinP >= effectiveMaxP) return { primary: [], secondary: [] };
    const smartTicks = calculateSmartTicks(effectiveMinP, effectiveMaxP, 'price', targetTicksY);
    return {
      primary: smartTicks.primary.filter(t => isWithin1DBounds(getY(t.value), bottomOfGrid, topOfGrid)),
      secondary: smartTicks.secondary.filter(t => isWithin1DBounds(getY(t.value), bottomOfGrid, topOfGrid))
    };
  }, [effectiveMinP, effectiveMaxP, getY, bottomOfGrid, topOfGrid, showGrid, targetTicksY, drawPriceLines]);

  // 2. Force Ticks for vertical grid lines (Z axis mapped to canvas X)
  const forceTicks = useMemo(() => {
    if (!showGrid || !drawForceLines) return { primary: [], secondary: [] };
    const maxForceVal = zLimit / zScale;
    const smartTicks = calculateSmartTicks(-maxForceVal, maxForceVal, 'force', targetTicksZ);
    return {
      primary: smartTicks.primary.filter(t => t.value >= -maxForceVal && t.value <= maxForceVal),
      secondary: smartTicks.secondary.filter(t => t.value >= -maxForceVal && t.value <= maxForceVal)
    };
  }, [showGrid, zLimit, zScale, targetTicksZ, drawForceLines]);

  // Initialize persistent canvas and texture once
  useEffect(() => {
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
      canvasRef.current.width = 1024;
      canvasRef.current.height = 1024;
    }
    if (!textureRef.current) {
      const tex = new THREE.CanvasTexture(canvasRef.current);
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      tex.generateMipmaps = false;
      textureRef.current = tex;
    }

    return () => {
      if (textureRef.current) {
        textureRef.current.dispose();
        textureRef.current = null;
      }
      canvasRef.current = null;
    };
  }, []);

  useEffect(() => {
    let animationFrameId: number;
    let timeoutId: NodeJS.Timeout;
    const draw = () => {
      if (!showGrid || !canvasRef.current || !textureRef.current) return;
      
      const heightWorld = topOfGrid - bottomOfGrid;
      if (heightWorld <= 0 || zLimit <= 0) return;

    const canvas = canvasRef.current;
    const canvasWidth = 1024;
    const canvasHeight = 1024;
    
    if (canvas.width !== canvasWidth || canvas.height !== canvasHeight) {
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // Draw Background Zones for Force (Buyers on left half, Sellers on right half to match floor extension)
    if (colorUp && colorDown) {
      // Left half: Buyers (Positive force, worldZ > 0) -> colorUp
      ctx.fillStyle = hexToRgba(colorUp, 0.04);
      ctx.fillRect(0, 0, canvasWidth / 2, canvasHeight);
      
      // Right half: Sellers (Negative force, worldZ < 0) -> colorDown
      ctx.fillStyle = hexToRgba(colorDown, 0.04);
      ctx.fillRect(canvasWidth / 2, 0, canvasWidth / 2, canvasHeight);
    }

    // Coordinate mapping functions
    const toCanvasX = (worldZ: number) => {
        // Buyers (+Z) should map to left (0), Sellers (-Z) to right (canvasWidth)
        return ((zLimit - worldZ) / (2 * zLimit)) * canvasWidth;
    };
    
    const toCanvasY = (worldY: number) => {
        return (1 - ((worldY - bottomOfGrid) / heightWorld)) * canvasHeight;
    };

    // Draw Price Lines (Horizontal)
    if (priceTicks.secondary.length > 0) {
      ctx.save();
      ctx.strokeStyle = hexToRgba(gridColor, gridOpacity * 0.45);
      ctx.lineWidth = Math.max(1, gridThickness * 0.8);
      ctx.setLineDash([2, 4]);
      ctx.beginPath();
      priceTicks.secondary.forEach(t => {
        const py = Math.floor(toCanvasY(getY(t.value))) + 0.5;
        if (py >= 0 && py <= canvasHeight) {
          ctx.moveTo(0, py);
          ctx.lineTo(canvasWidth, py);
        }
      });
      ctx.stroke();
      ctx.restore();
    }

    if (priceTicks.primary.length > 0) {
      ctx.save();
      ctx.strokeStyle = hexToRgba(gridColor, gridOpacity * 0.85);
      ctx.lineWidth = Math.max(1, gridThickness);
      ctx.setLineDash([]);
      ctx.beginPath();
      priceTicks.primary.forEach(t => {
        const py = Math.floor(toCanvasY(getY(t.value))) + 0.5;
        if (py >= 0 && py <= canvasHeight) {
          ctx.moveTo(0, py);
          ctx.lineTo(canvasWidth, py);
        }
      });
      ctx.stroke();
      ctx.restore();
    }

    // Draw Force Lines (Vertical)
    if (forceTicks.secondary.length > 0) {
      ctx.save();
      ctx.strokeStyle = hexToRgba(gridColor, gridOpacity * 0.45);
      ctx.lineWidth = Math.max(1, gridThickness * 0.8);
      ctx.setLineDash([2, 4]);
      ctx.beginPath();
      forceTicks.secondary.forEach(t => {
        if (t.value === 0) return;
        const px = Math.floor(toCanvasX(t.value * zScale)) + 0.5;
        if (px >= 0 && px <= canvasWidth) {
          ctx.moveTo(px, 0);
          ctx.lineTo(px, canvasHeight);
        }
      });
      ctx.stroke();
      ctx.restore();
    }

    if (forceTicks.primary.length > 0) {
      ctx.save();
      forceTicks.primary.forEach(t => {
        const px = Math.floor(toCanvasX(t.value * zScale)) + 0.5;
        
        if (px >= 0 && px <= canvasWidth) {
          ctx.beginPath();
          if (t.value === 0) {
             ctx.strokeStyle = gridColor;
             ctx.globalAlpha = Math.min(1, gridOpacity * 1.5);
             ctx.lineWidth = Math.max(2, gridThickness * 1.5);
             ctx.setLineDash([4, 4]);
          } else {
             ctx.strokeStyle = hexToRgba(gridColor, gridOpacity * 0.85);
             ctx.lineWidth = Math.max(1, gridThickness);
             ctx.setLineDash([]);
             ctx.globalAlpha = 1.0;
          }
          
          ctx.moveTo(px, 0);
          ctx.lineTo(px, canvasHeight);
          ctx.stroke();
        }
      });
      ctx.restore();
    }

    textureRef.current.needsUpdate = true;
    };
    timeoutId = setTimeout(() => { animationFrameId = requestAnimationFrame(draw); }, 1500);
    return () => { clearTimeout(timeoutId); if (animationFrameId) cancelAnimationFrame(animationFrameId); };
  }, [
    priceTicks, forceTicks,
    getY, zScale, bottomOfGrid, topOfGrid, zLimit,
    showGrid, gridColor, gridOpacity, gridThickness,
    colorUp, colorDown
  ]);

  if (!textureRef.current || !showGrid) return null;

  return (
    <mesh 
      ref={meshRef}
      position={[sideX, (topOfGrid + bottomOfGrid) / 2, 0]} 
      rotation={[0, Math.PI / 2, 0]}
    >
      <planeGeometry args={[2 * zLimit, topOfGrid - bottomOfGrid]} />
      <meshBasicMaterial 
        map={textureRef.current} 
        transparent={true} 
        depthWrite={false} 
        side={THREE.DoubleSide} 
        toneMapped={false}
      />
    </mesh>
  );
};
